import { CHECKLIST_ITEM_STATUS_TYPE } from "@/other/constants/OtherKeys.jsx";
import {
  createChecklist,
  createChecklistItem as buildChecklistItem,
  type Checklist,
  type ChecklistItem
} from "../types";

/** Row from GET /board/:id/checklists/list (entity-filtered or board-wide). */
export type BoardChecklistListRow = {
  id: number;
  entity_title?: string | null;
  checklist_title?: string | null;
  type?: number;
  created_at?: string;
  total_items_count?: number;
  item_checked_count?: number;
  board_id?: number;
  assigned_to?: Array<number | string>;
  has_permission?: boolean;
  color?: string | null;
  task?: number | null;
  task_id?: number | null;
  resource?: number | null;
  resource_id?: number | null;
  client?: number | null;
  client_id?: number | null;
  recurrence?: string;
  recurrence_weekdays?: number[] | null;
  can_view_grant?: number;
  can_edit_grant?: number;
  archived?: boolean;
};

/** GET /board/<board_id>/checklists/<id> payload (HQ retrieve). */
export type ChecklistRetrievePayload = {
  id: number;
  entity_title?: string | null;
  checklist_title?: string;
  title?: string;
  type?: number;
  order?: number;
  created_at?: string;
  updated_at?: string;
  creator?: number;
  updater?: number;
  archived?: boolean;
  board?: number;
  task?: number | null;
  task_id?: number | null;
  resource?: number | null;
  resource_id?: number | null;
  client?: number | null;
  client_id?: number | null;
  nudge_date?: string | null;
  can_edit_grant?: number;
  can_view_grant?: number;
  assigned_boarduser_ids?: number[];
  assigned_boardusers?: number[];
  has_permission?: boolean;
  total_items?: number;
  items_left?: number;
  recurrence?: string;
  recurrence_weekdays?: number[] | null;
  items?: Array<{
    id: number;
    description?: string;
    comment?: string | null;
    comment_by?: string | null;
    comment_date?: string | null;
    status?: number;
    checked?: boolean;
    checked_by?: string | null;
    checked_date?: string | null;
    image_url?: string | null;
    thumbnail_url?: string | null;
    board_id?: number;
    order?: number;
    archived?: boolean;
    created_at?: string;
  }>;
};

export type ChecklistDetail = {
  checklist: Checklist;
  items: ChecklistItem[];
};

export type ChecklistDetailMapOptions = {
  assignedBoardUserIds?: number[];
  canEditGrant?: number;
  canViewGrant?: number;
};

function numericAssigneeIds(ids: number[] | undefined): number[] {
  if (!Array.isArray(ids) || !ids.length) return [];
  return ids.filter((id): id is number => typeof id === "number");
}

/** Merge PATCH/POST checklist record response into an existing Checklist. */
export function mergeChecklistRecordWrite(
  existing: Checklist,
  data: {
    id: number;
    title?: string;
    order?: number;
    created_at?: string;
    updated_at?: string;
    creator?: number;
    updater?: number;
    archived?: boolean;
    resource?: number | null;
    client?: number | null;
    task?: number | null;
    nudge_date?: string | null;
    can_edit_grant?: number;
    can_view_grant?: number;
    assigned_boardusers?: number[];
    recurrence?: string;
    recurrence_weekdays?: number[] | null;
  }
): Checklist {
  return {
    ...existing,
    id: data.id,
    title: data.title ?? existing.title,
    archived: data.archived != null ? Boolean(data.archived) : existing.archived,
    order: data.order ?? existing.order,
    createdAt: data.created_at ?? existing.createdAt,
    updatedAt: data.updated_at ?? existing.updatedAt,
    creator: data.creator ?? existing.creator,
    updater: data.updater ?? existing.updater,
    canEdit: data.can_edit_grant ?? existing.canEdit,
    canView: data.can_view_grant ?? existing.canView,
    recurrence: data.recurrence ?? existing.recurrence,
    recurrenceWeekdays:
      data.recurrence_weekdays !== undefined ? data.recurrence_weekdays : existing.recurrenceWeekdays,
    nudgeDate: data.nudge_date !== undefined ? data.nudge_date : existing.nudgeDate,
    assignedBoardUsers:
      data.assigned_boardusers !== undefined
        ? numericAssigneeIds(data.assigned_boardusers)
        : existing.assignedBoardUsers,
    taskId: data.task !== undefined ? data.task : existing.taskId,
    resourceId: data.resource !== undefined ? data.resource : existing.resourceId,
    clientId: data.client !== undefined ? data.client : existing.clientId
  };
}

/** Map HQ retrieve payload → checklist + items for the tile/editor. */
export function checklistDetailFromRetrieve(
  data: ChecklistRetrievePayload,
  options: ChecklistDetailMapOptions = {}
): ChecklistDetail {
  const { assignedBoardUserIds, canEditGrant, canViewGrant } = options;
  const fromIds = numericAssigneeIds(data.assigned_boarduser_ids);
  const fromUsers = numericAssigneeIds(data.assigned_boardusers);
  const assigneeIds = assignedBoardUserIds?.length
    ? assignedBoardUserIds
    : fromIds.length
      ? fromIds
      : fromUsers;

  const checklist = createChecklist({
    id: data.id,
    title: data.checklist_title ?? data.title ?? "",
    entityTitle: data.entity_title ?? undefined,
    taskId: data.task ?? data.task_id ?? null,
    resourceId: data.resource ?? data.resource_id ?? null,
    clientId: data.client ?? data.client_id ?? null,
    archived: Boolean(data.archived),
    canEdit: data.can_edit_grant ?? canEditGrant,
    canView: data.can_view_grant ?? canViewGrant,
    order: data.order ?? 0,
    recurrence: data.recurrence ?? "none",
    recurrenceWeekdays: data.recurrence_weekdays ?? null,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
    creator: data.creator,
    updater: data.updater,
    nudgeDate: data.nudge_date ?? null,
    assignedBoardUsers: assigneeIds,
    totalItems: data.total_items,
    itemsLeft: data.items_left
  });

  const items = (data.items ?? []).map((item, index) => {
    const status = item.status ?? CHECKLIST_ITEM_STATUS_TYPE.UNSEEN;
    const checked =
      item.checked !== undefined ? Boolean(item.checked) : status === CHECKLIST_ITEM_STATUS_TYPE.COMPLETED;
    return buildChecklistItem({
      id: item.id,
      description: item.description ?? "",
      status,
      checked,
      checkedBy: item.checked_by ?? null,
      checkedDate: item.checked_date ?? null,
      checklistId: data.id,
      order: item.order ?? index,
      archived: Boolean(item.archived),
      comment: item.comment ?? null,
      createdAt: item.created_at ?? item.checked_date ?? null,
      image: item.image_url ?? undefined,
      thumbnailUrl: item.thumbnail_url ?? undefined,
      checklist
    });
  });

  return { checklist, items };
}

export type ChecklistFromBoardListOptions = {
  entityType?: "task" | "resource" | "client";
  entityId?: number | string;
};

/** Map GET /board/:id/checklists/list row → Checklist (entity tile or board-wide list). */
export function checklistFromBoardListRow(
  row: BoardChecklistListRow,
  options: ChecklistFromBoardListOptions = {}
): Checklist {
  const { entityType, entityId } = options;
  const assigneeIds = Array.isArray(row.assigned_to)
    ? row.assigned_to.filter((id): id is number => typeof id === "number")
    : [];

  const taskId =
    row.task_id ?? row.task ?? (entityType === "task" ? entityId ?? null : null);
  const resourceId =
    row.resource_id ?? row.resource ?? (entityType === "resource" ? entityId ?? null : null);
  const clientId =
    row.client_id ?? row.client ?? (entityType === "client" ? entityId ?? null : null);

  return createChecklist({
    id: row.id,
    title: row.checklist_title ?? "",
    createdAt: row.created_at,
    entityTitle: row.entity_title ?? "",
    itemsLeft: Math.max((row.total_items_count ?? 0) - (row.item_checked_count ?? 0), 0),
    totalItems: row.total_items_count ?? 0,
    assignedBoardUsers: assigneeIds,
    taskId,
    resourceId,
    clientId,
    archived: Boolean(row.archived),
    canEdit: row.can_edit_grant,
    canView: row.can_view_grant,
    recurrence: row.recurrence ?? "none",
    recurrenceWeekdays: row.recurrence_weekdays ?? null
  });
}
